import { useEffect, useState } from 'react';
import { appConfig } from '../library/utils/appConfig';
import { getAuthToken } from '../library/api/authToken';

// Key names match the brg-consumer-assistant spec so tokens are shared
// with any other BRG chatkit instance on the same origin.
const AID_KEY = 'chatbot_aid';
const SESS_KEY = 'chatbot_sess';
const TOKEN_HEADER = 'X-User-Token';

const PROXY_URL = appConfig.proxyUrl;

export interface SessionTokens {
    aid: string;
    sess: string;
}

function authHeaders(): Record<string, string> {
    const token = getAuthToken();
    return token ? { [TOKEN_HEADER]: token } : {};
}

async function createSession(aid: string): Promise<SessionTokens> {
    const res = await fetch(`${PROXY_URL}/session`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Chatbot-Aid': aid,
            ...authHeaders(),
        },
        body: JSON.stringify({ brand: 'chefsteps', locale: 'en-US' }),
    });
    if (!res.ok) throw new Error(`session: ${res.status}`);
    const { aid: newAid, sess } = await res.json();
    return { aid: newAid ?? aid, sess };
}

async function validateSession(aid: string, sess: string): Promise<SessionTokens> {
    const res = await fetch(`${PROXY_URL}/session/validate`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Chatbot-Aid': aid,
            'X-Chatbot-Sess': sess,
            ...authHeaders(),
        },
    });
    if (!res.ok) throw new Error(`validate: ${res.status}`);
    const { sess: newSess } = await res.json();
    return { aid, sess: newSess };
}

export function useSession() {
    const [tokens, setTokens] = useState<SessionTokens | null>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        initSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function initSession() {
        const aid = localStorage.getItem(AID_KEY) ?? crypto.randomUUID();
        const existingSess = localStorage.getItem(SESS_KEY);

        try {
            let result: SessionTokens;
            if (existingSess) {
                try {
                    result = await validateSession(aid, existingSess);
                } catch {
                    localStorage.removeItem(SESS_KEY);
                    result = await createSession(aid);
                }
            } else {
                result = await createSession(aid);
            }
            localStorage.setItem(AID_KEY, result.aid);
            localStorage.setItem(SESS_KEY, result.sess);
            setTokens(result);
        } catch {
            // chat will surface error on first send attempt
        } finally {
            setReady(true);
        }
    }

    function refreshSess(newSess: string) {
        localStorage.setItem(SESS_KEY, newSess);
        setTokens(prev => prev ? { ...prev, sess: newSess } : prev);
    }

    return { tokens, ready, refreshSess };
}
